import { useEffect, useMemo, useState } from "react";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { AppCard } from "../../../../components/common/card";
import { Input } from "../../../../components/common/input";
import { Select } from "../../../../components/forms/select";
import { Button } from "../../../../components/common/button";
import { Text } from "../../../../components/common/typography";
import { dollarFormat } from "../../../../utils/currencies";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { useCurrencies } from "../../../../hooks/useCurrencies";
import {
  internalDeliveryCostService,
  type InternalDeliveryCostSource,
} from "../../data/internal-delivery-cost.service";

type ShipmentLike = {
  id: number;
  name: string;
  internalDeliveryCostEstimate?: number | null;
  internalDeliveryCostCurrency?: string | null;
  internalDeliveryActualCost?: number | null;
  internalDeliveryActualCostCurrency?: string | null;
  internalDeliveryCostSource?: string | null;
};

interface Props {
  purchaseOrderId: number;
  shipments?: ShipmentLike[] | null;
  onSaved?: () => void | Promise<void>;
}

type Draft = {
  actualCost: string;
  currency: string;
  costSource: InternalDeliveryCostSource;
  saving: boolean;
};

const COST_SOURCE_OPTIONS: Array<{ value: InternalDeliveryCostSource; label: string }> = [
  { value: "manual", label: "Manual" },
  { value: "supplier_invoice", label: "Supplier invoice" },
  { value: "carrier_invoice", label: "Carrier invoice" },
  { value: "rate_card", label: "Rate card" },
];

function formatEstimate(amount?: number | null, currency?: string | null): string {
  if (amount == null) return "—";
  return dollarFormat(amount, currency ?? "QAR");
}

export function InternalDeliveryCostEditor({ purchaseOrderId, shipments, onSaved }: Props) {
  const { currencyOptions } = useCurrencies();

  const initialDrafts = useMemo(() => {
    const entries = (shipments ?? []).map((shipment) => [
      shipment.id,
      {
        actualCost: shipment.internalDeliveryActualCost?.toString() ?? "",
        currency: shipment.internalDeliveryActualCostCurrency ?? shipment.internalDeliveryCostCurrency ?? "QAR",
        costSource: (shipment.internalDeliveryCostSource as InternalDeliveryCostSource) ?? "manual",
        saving: false,
      } satisfies Draft,
    ]);
    return Object.fromEntries(entries) as Record<number, Draft>;
  }, [shipments]);

  const [drafts, setDrafts] = useState<Record<number, Draft>>(initialDrafts);

  useEffect(() => {
    setDrafts(initialDrafts);
  }, [initialDrafts]);

  const updateDraft = (id: number, patch: Partial<Draft>) => {
    setDrafts((current) => ({
      ...current,
      [id]: {
        ...(current[id] ?? {}),
        actualCost: "",
        currency: "QAR",
        costSource: "manual",
        saving: false,
        ...patch,
      },
    }));
  };

  const handleSave = async (shipment: ShipmentLike) => {
    const draft = drafts[shipment.id];
    if (!draft) return;

    const parsedCost = Number(draft.actualCost);
    if (!Number.isFinite(parsedCost) || parsedCost < 0) {
      showErrorToast("Enter a valid delivery cost.");
      return;
    }

    updateDraft(shipment.id, { saving: true });
    try {
      await internalDeliveryCostService.upsertActualCost(shipment.id, {
        orderType: "purchase_order",
        orderId: purchaseOrderId,
        actualCost: parsedCost,
        currency: draft.currency || shipment.internalDeliveryCostCurrency || "QAR",
        costSource: draft.costSource,
      });
      showSuccessToast(`Recorded actual cost for ${shipment.name}`);
      await onSaved?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save internal delivery cost.";
      showErrorToast(message);
    } finally {
      updateDraft(shipment.id, { saving: false });
    }
  };

  if (!shipments || shipments.length === 0) {
    return null;
  }

  return (
    <AppCard
      title="Internal Delivery Cost"
      color="bg-bgWhite"
      borderColor="border-borderDefault"
      className="w-full"
    >
      <div className="flex flex-col gap-4">
        <Text variant="bodySm" tone="muted">
          Record actual internal delivery cost after reconciliation. This is for Procurtal admin use only.
        </Text>

        <div className="flex flex-col gap-3">
          {shipments.map((shipment, index) => {
            const draft = drafts[shipment.id] ?? {
              actualCost: "",
              currency: shipment.internalDeliveryCostCurrency ?? "QAR",
              costSource: "manual" as InternalDeliveryCostSource,
              saving: false,
            };

            return (
              <div key={shipment.id} className="rounded-xl border border-borderDefault bg-bgLight p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <Text variant="bodyBold" tone="dark">
                      Shipment {index + 1}
                    </Text>
                    <Text variant="bodySm" tone="muted">
                      Estimated: {formatEstimate(shipment.internalDeliveryCostEstimate, shipment.internalDeliveryCostCurrency)}
                    </Text>
                  </div>
                  {shipment.internalDeliveryActualCost != null ? (
                    <Text variant="bodySmBold" tone="dark">
                      Recorded: {dollarFormat(
                        shipment.internalDeliveryActualCost,
                        shipment.internalDeliveryActualCostCurrency ?? shipment.internalDeliveryCostCurrency ?? "QAR",
                      )}
                    </Text>
                  ) : (
                    <Text variant="bodySm" tone="muted">No actual cost recorded yet</Text>
                  )}
                </div>

                <div className="grid items-end gap-3 md:grid-cols-[1fr_160px_180px_auto]">
                  <Input
                    name={`actual-cost-${shipment.id}`}
                    label="Actual Cost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={draft.actualCost}
                    onChange={(e) => updateDraft(shipment.id, { actualCost: e.target.value })}
                  />
                  <Select
                    name={`actual-currency-${shipment.id}`}
                    label="Currency"
                    value={draft.currency}
                    onChange={(e) => updateDraft(shipment.id, { currency: e.target.value })}
                    options={currencyOptions}
                    density="compact"
                  />
                  <Select
                    name={`actual-source-${shipment.id}`}
                    label="Source"
                    value={draft.costSource}
                    onChange={(e) =>
                      updateDraft(shipment.id, { costSource: e.target.value as InternalDeliveryCostSource })
                    }
                    options={COST_SOURCE_OPTIONS}
                    density="compact"
                  />
                  <Button
                    variant="primary"
                    size="md"
                    leftIcon={<IconDeviceFloppy size={18} />}
                    disabled={draft.saving}
                    onClick={() => void handleSave(shipment)}
                    className="h-10"
                  >
                    {draft.saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppCard>
  );
}
